/**
 * A BIZONYLAT FÁJLJA — az aláírt címe és az, hogy MI AZ.
 *
 * MIÉRT KELL EGYÁLTALÁN ALÁÍRT CÍM. A hitelesítés `Authorization` fejlécen
 * megy, egy `<a href>` navigáció viszont NEM TUD fejlécet küldeni — a fájlra
 * mutató link ezért 401-et kapott, és a „Megnyitom" soha nem működött. A
 * szerver ad egy öt percig élő, EGYETLEN bizonylathoz kötött aláírt címet; az
 * mehet a `href`-be. Ugyanez áll az `<img src>`-re és az `<iframe src>`-re is:
 * azok is fejléc nélküli böngésző-betöltések.
 *
 * MIÉRT ELŐRE KÉRJÜK EL, és nem kattintásra: kattintásra kérve a link
 * `async` lenne, és a `window.open` a válasz megérkezésekor már NEM a
 * felhasználói gesztus alatt futna — a böngésző popup-blokkolója megfogná.
 * Előre elkérve viszont a link VALÓDI link marad: középső gombbal és
 * `Ctrl`-lal is nyitható, és a böngésző csinálja, nem a JavaScript.
 *
 * MIÉRT JÖN A TÍPUS IS. Az „Eredeti" panel BEÁGYAZVA mutatja a papírt
 * (`§8c`/8: „tárolódik és látszik"), és a beágyazás elemet választ: képhez
 * `<img>`, PDF-hez `<iframe>`. A választás a betöltés MEGKEZDÉSE előtt
 * történik, tehát a válasz `Content-Type` fejlécéből már késő volna
 * megtudni — a szerver ezért a cím mellé adja.
 *
 * A CÍM A BIZONYLATHOZ TARTOZIK, nem a panelhez: bizonylatot váltva az előző
 * címet eldobjuk. Egy versenyhelyzetben érkező régi válasz sem írhatja felül
 * az újat — ezért van generáció, ugyanúgy, mint a lista betöltésénél.
 *
 * EGY BIZONYLAT, EGY KÉRÉS. A horgot a munkalista EGY helyen hívja meg, és az
 * eredményt adja tovább a fejlécnek és az „Eredeti" panelnek is. Mindkét
 * helyen külön meghívva ugyanarra a papírra két aláírt cím készülne, két
 * hálózati körrel — és a kettő nem is ugyanabban a pillanatban járna le.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';

export interface DocumentFile {
  /** Az aláírt, öt percig élő cím — `href`, `src`. */
  url: string;
  /** Amit a szerver a fájlra mond. Ebből választ elemet az „Eredeti" panel. */
  mimeType: string;
  /** A feltöltéskori név — a panel fejlécén ez azonosítja a papírt. */
  originalFilename: string;
}

/**
 * HÁROM ÁLLAPOT, MERT HÁROM VAN — és a `null` csak KETTŐT tudott elmondani.
 *
 * Az első változat egyetlen `DocumentFile | null`-t adott vissza, és a panel a
 * `null`-ra kiírta, hogy „a bizonylat képe most nem tölthető be". Csakhogy a
 * `null` a TÖLTÉST is jelentette: a horog minden bizonylatváltásnál nullázott,
 * tehát sor-módban MINDEN `Enter` után felvillant egy hibaüzenet egy olyan
 * betöltésről, ami éppen rendben zajlott. Lassú vonalon másodpercekig állt ott.
 *
 * A panel szabálya az, hogy a néma üres keret a legrosszabb kimenet — de a
 * magabiztosan HAMIS mondat a második legrosszabb, és azt csináltuk.
 * *(Code review fogta meg, 09-04.)*
 */
export type DocumentFileState =
  | { status: 'loading' }
  | { status: 'ready'; file: DocumentFile }
  /** A „nincs meg" és a „nem jött meg" itt szándékosan EGY ág — lásd lent. */
  | { status: 'failed' };

/**
 * MENNYIT VÁRUNK, MIELŐTT EGYÁLTALÁN KÉRÜNK.
 *
 * A `J`/`K` lépegetés ISMÉTLŐDIK, amíg a billentyű le van nyomva, és minden
 * lépés új bizonylatra visz. Késleltetés nélkül a kupacon végigfutva minden
 * ÁTHALADT papírra elindulna egy aláírt cím és utána MAGA A FÁJL is — a
 * feltöltési korlát 25 MiB (`MAX_UPLOAD_BYTES`), tehát egy 200 soros kupac
 * végigpörgetése nagyságrendekkel több adatot húzna le, mint amennyit a
 * könyvelő megnéz. A böngésző a félbehagyott letöltéseket megszakítja, de a
 * kéréseket már elindította.
 *
 * 200 ms az a küszöb, ami alatt a lépés még „átfutás", fölötte már „megálltam
 * ezen". Aki egy sorra rálép és ott marad, nem vesz észre belőle semmit.
 * *(Code review fogta meg, 09-04.)*
 */
const SETTLE_MS = 200;

export function useDocumentFile(documentId: string | null): DocumentFileState {
  const [state, setState] = useState<DocumentFileState>({ status: 'loading' });

  useEffect(() => {
    if (documentId === null) {
      // NINCS KIJELÖLT BIZONYLAT — nincs mit tölteni, és nincs mit bukni. A
      // panelt ilyenkor a munkalista ki sem teszi.
      setState({ status: 'failed' });
      return;
    }

    let current = true;
    setState({ status: 'loading' });

    const timer = setTimeout(() => {
      void api<DocumentFile>(`/documents/${documentId}/file-url`)
        .then((signed) => {
          if (current) {
            // A SZERVER TELJES ÚTVONALAT AD (#19 óta az `/api` előtag a
            // backendé), tehát nem teszünk elé semmit. Korábban itt állt össze
            // a cím két félből — az előtag a felületé, a többi a backendé —, és
            // a token útvonal-kötése mellett az kétértelmű lett volna.
            setState({ status: 'ready', file: signed });
          }
        })
        .catch(() => {
          // A HIBÁT NEM MONDJUK KI KÜLÖN, és a „nincs ilyen bizonylat"
          // (404) meg a hálózati bukás sem válik szét: egyik sem a könyvelő
          // döntése, és egyikkel sincs mit tennie. A bizonylattal dolgozni
          // enélkül is lehet — a lefűzéshez nem kell megnyitni a papírt.
          if (current) {
            setState({ status: 'failed' });
          }
        });
    }, SETTLE_MS);

    return () => {
      current = false;
      // A LE NEM JÁRT IDŐZÍTŐ IS TÖRLENDŐ, különben az áthaladt bizonylat
      // kérése 200 ms múlva mégis elindulna — épp az, amit el akartunk kerülni.
      clearTimeout(timer);
    };
  }, [documentId]);

  return state;
}
